import { Plan_ExercisePath, Plan_SubSlot } from '@cf-shared/data-model/fitness-plan';
import { deepClone } from '@cf-shared/deep-helpers';
import { UiExercise } from '../../models/workout-ui-models';
import { snapshotConcludedDate } from '../../util/historical-subslot';

/**
 * The volume adjustment an accepted volume card steps from, read off `answerTarget` — the
 * sub-slot the accept also reads today's baseline from and writes its answer back into.
 *
 * `planPath` is not that object on a finished workout: `setCurrentPath` points it into the
 * session's own snapshot (`logExercise.planSubslotBackup`), while the answer goes to the
 * program. The two can hold different numbers, because typing a set count by hand resets the
 * adjustment on the program alone. Stepping from the snapshot's stale number then makes the
 * accepted change do nothing, or go the wrong way (df-06e16b428c66). See
 * `design-docs/volume-autoregulation.md`, "The baseline can move while a suggestion is
 * unanswered".
 *
 * Falls back to `planPath` when the target holds no path for this intensity, so an exercise
 * with nowhere else to read from behaves as it always did.
 */
export function storedVolumeAdjustmentForAnswer(
  e: UiExercise,
  answerTarget: Plan_SubSlot,
): -1 | 0 | 1 | undefined {
  const targetPath = answerTarget.exercisePaths?.find(p => p.intensityLevel === e.planPath.intensityLevel);
  return (targetPath ?? e.planPath).volumeAdjustment;
}

/**
 * Clearing the "a volume-autoregulation question is open" state off one exercise, once the user
 * has answered it. Both the accept and the decline branch of `handleAutoregulationMessage` end
 * here, so the two cannot drift apart — before #4531 each spelled the same five deletes out by
 * hand, in slightly different ways.
 *
 * These five fields are control state, not plan history: together they say "a question is open,
 * raised by that workout, at that set, offering that number". Once the user has answered, a
 * container still holding them is not preserving what the workout prescribed — it is claiming a
 * question is open when it is not, which suppresses the next card and disarms the self-heal.
 * That is why they are cleared everywhere the path lives, including the workout's own copy of
 * the plan. See `design-docs/volume-autoregulation.md`, "The containers a path lives in".
 */
export const AUTOREGULATION_FIELDS = [
  'autoregulationWorkoutLog_id',
  'autoregulationType',
  'autoregulationTriggerSetIndex',
  'autoregulationNewVolumeAdjustment',
  'autoregulationBaselineSets',
] as const;

/**
 * Delete every open-question field from ONE path, and report whether any of them had been set.
 *
 * This is the only way to reach the field list, so a field added to it is cleared by every caller
 * at once. Before #4531 the list was spelled out by hand in six places; two of those spellings
 * outlived that change, because they clear a backup belonging to a DIFFERENT workout's document
 * and so cannot go through `resolveAutoregulationOnExercise` below. They call this instead.
 *
 * The return value says "this path really was claiming a question was open" — a caller that has to
 * save the document holding the path uses it to avoid a pointless write. A caller that already
 * knows it must save can ignore it.
 */
export function clearAutoregulationFields(path: Plan_ExercisePath): boolean {
  let cleared = false;
  for (const field of AUTOREGULATION_FIELDS) {
    if (path[field] !== undefined) {
      cleared = true;
    }
    delete path[field];
  }
  return cleared;
}

/**
 * Clear the open-question flags for this exercise's CURRENT intensity, in every container that
 * path lives in — the plan sub-slot AND the copy of the plan the workout log carries.
 *
 * `syncVolumeAdjustment` is for the accept branch: it has already worked the accepted adjustment
 * out and written it onto `planPath`, and every other container holding this same path has to end
 * up with the same number. The decline branch changes no volume, so it passes false.
 *
 * It cannot see a path belonging to a DIFFERENT workout — the copy held by the workout that
 * RAISED the question is cleared by its own block in `handleAutoregulationMessage`, which can load
 * that document. That block is deliberately left alone: an earlier workout's copy must keep the
 * volume it prescribed, so only the answered question is cleared there.
 *
 * Returns true when it changed a path inside `logExercise.planSubslotBackup` or
 * `logExercise.planSubslotAfterSession` — both copies are part of the workout document on screen, so the caller has to save that document or the clear is lost
 * when the user leaves the page.
 */
export function resolveAutoregulationOnExercise(
  e: UiExercise,
  syncVolumeAdjustment: boolean,
): boolean {
  if (!e.planPath) {
    return false;
  }

  const acceptedVolumeAdjustment = e.planPath.volumeAdjustment;
  const backupPaths = e.logExercise.planSubslotBackup?.exercisePaths ?? [];
  let changedViewedBackup = false;

  for (const path of e.livePathsForCurrentIntensity()) {
    let changed = false;

    if (syncVolumeAdjustment && path.volumeAdjustment !== acceptedVolumeAdjustment) {
      path.volumeAdjustment = acceptedVolumeAdjustment;
      changed = true;
    }
    if (clearAutoregulationFields(path)) {
      changed = true;
    }

    if (changed && backupPaths.includes(path)) {
      changedViewedBackup = true;
    }
  }

  // the "as the session left it" copy was taken when the set concluded, before the user answered.
  // An accepted set-count change is part of where the session left the exercise, so carry it there
  // too. The copy never holds the question itself (`buildAfterSessionSnapshot` strips it).
  const afterSessionPath = e.logExercise.planSubslotAfterSession?.exercisePaths
    ?.find(p => p.intensityLevel === e.planPath.intensityLevel);
  if (syncVolumeAdjustment && afterSessionPath && afterSessionPath.volumeAdjustment !== acceptedVolumeAdjustment) {
    afterSessionPath.volumeAdjustment = acceptedVolumeAdjustment;
    changedViewedBackup = true;
  }

  return changedViewedBackup;
}

/**
 * The exercise's program entry as this session LEFT it, for `Log_WorkoutExercise.planSubslotAfterSession`
 * (df-5dbef4889250). Taken at the end of every set conclusion, so the last one wins.
 *
 * Why it exists: `planSubslotBackup` is taken at the FIRST set, so for a strength-test session it
 * holds the bare test the session started from. After an app restart that was the only record
 * left, and swapping the exercise away and back made the user repeat a test they had just passed.
 *
 * The copy is read later by the layoff restore, which puts its paths back onto a fresh program
 * entry. So it must not carry a question that is only open NOW: a plateau pointer or a
 * volume-autoregulation question raised in this session would be reopened days later, after the
 * user has answered it. Both are stripped, the same way the split before an exercise switch strips
 * them from its copy.
 *
 * `concluded_utc` is the date the copy carries, decided by `snapshotConcludedDate` exactly as for
 * `planSubslotBackup`, so an older session never reads as the exercise's latest.
 */
export function buildAfterSessionSnapshot(liveEntry: Plan_SubSlot, concluded_utc: string | undefined): Plan_SubSlot {
  const snapshot = deepClone(liveEntry);
  if (concluded_utc) {
    snapshot.lastTimeConcluded_utc = concluded_utc;
  } else {
    delete snapshot.lastTimeConcluded_utc;
  }
  delete snapshot.plateauWorkoutLog_id;
  for (const path of snapshot.exercisePaths ?? []) {
    clearAutoregulationFields(path);
  }
  return snapshot;
}

/**
 * The copy to store in `logExercise.planSubslotAfterSession` after a set conclusion, or undefined
 * when this conclusion must not record one (df-5dbef4889250).
 *
 * Taken only from the program's own entry. The page may write that entry (an ordinary live
 * session), or this finished session still owns the exercise's progression and the correction has
 * just moved it (#5276). Nothing is taken from a finished session's historical copy (#4939), for an
 * exercise the program no longer holds (#4614), or when the entry now holds a different exercise:
 * none of those is "where this session left the exercise", and the layoff restore would hand that
 * state back later. The date is decided by `snapshotConcludedDate`, as for `planSubslotBackup`.
 */
export function afterSessionSnapshotFor(e: UiExercise, workoutTime_utc: string | undefined): Plan_SubSlot | undefined {
  const liveEntry = afterSessionSource(e);
  if (!liveEntry || liveEntry.exercise_id !== e.logExercise.exerciseId) {
    return undefined;
  }
  return buildAfterSessionSnapshot(
    liveEntry,
    snapshotConcludedDate(liveEntry.lastTimeConcluded_utc, workoutTime_utc, !!e.startedInFuture));
}

/** The program entry the copy is taken from, or undefined when this conclusion has none it may
 *  speak for. See `afterSessionSnapshotFor`. */
function afterSessionSource(e: UiExercise): Plan_SubSlot | undefined {
  if (e.canWritePlanSubSlotBackToPlan()) {
    return e.livePlanSubSlot ?? e.planSubSlot;
  }
  // a correction in the finished session that still owns the exercise's progression (#5276) moves
  // the live program, so the copy must follow it — otherwise a later restore brings back the
  // progression the user just corrected away. The page's own copy is historical here, so only the
  // program's entry will do.
  if (e.ownsLiveProgression && !e.planSubSlotMissingFromPlan) {
    return e.livePlanSubSlot;
  }
  return undefined;
}
